import { shapes } from '@joint/plus';
import CodeIcon from '@mui/icons-material/Code';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import PlaceholderPicker from '../PropertyPanel/PlaceholderPicker';
import { insertAtCursor } from '../PropertyPanel/insertAtCursor';
import { useBaseInspector } from './useBaseInspector';

/**
 * NoteInspector — Item #2 roadmap chatbot JOINT (07/10/2026).
 *
 * Caixa SÍNCRONA que cria uma nota interna no chat (`tb_2talk_chat_notes`). Texto
 * configurado pelo admin suporta placeholders — resolvidos antes do INSERT pelo backend
 * via IPlaceholderResolver (padrão único do projeto).
 *
 * UX simples: TextField multiline + botão {} abre PlaceholderPicker (reaproveita o do
 * ConditionInspector / SetVariableInspector / ServicePropertiesPanel).
 *
 * Persistência: `attrs/noteText/text` (string). Backend lê diretamente, resolve, insere.
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    noteText: ['attrs', 'noteText', 'text'],
};

const NoteInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [noteText, setNoteText] = useState<string>('');

    const isSyncingRef = useRef(false);
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;
        setLabel(cell.prop(cellProps.label) ?? '');
        setNoteText(cell.prop(cellProps.noteText) ?? '');
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persistNoteText = (v: string) => {
        isSyncingRef.current = true;
        try {
            changeCellProp(cellProps.noteText, v);
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'note.title', defaultMessage: 'Nota interna' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'note.title.hint',
                        defaultMessage: 'Adiciona uma nota interna à conversa (visível só pros atendentes). Suporta placeholders como @variavel@, {{chat.customer.name}}, {{system.datetime}}.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                <TextField
                    label={intl.formatMessage({ id: 'note.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder="Nota"
                />

                <TextField
                    label={intl.formatMessage({ id: 'note.text', defaultMessage: 'Texto da nota' })}
                    size="small"
                    multiline
                    minRows={4}
                    maxRows={10}
                    value={noteText}
                    onChange={(e) => {
                        setNoteText(e.target.value);
                        persistNoteText(e.target.value);
                    }}
                    inputRef={(el) => { activeInputRef.current = el; }}
                    placeholder={intl.formatMessage({
                        id: 'note.text.placeholder',
                        defaultMessage: 'Ex.: Cliente @customer.name@ interessado em plano {{variable.plano}}. Captado em {{system.datetime}}.',
                    })}
                    helperText={intl.formatMessage({
                        id: 'note.text.help',
                        defaultMessage: 'Placeholders disponíveis: @variavel@, {{chat.*}}, {{system.*}}, {{session.*}}, %param%, $custom$. Clique no ícone pra inserir.',
                    })}
                    InputProps={{
                        endAdornment: (
                            <IconButton
                                size="small"
                                onClick={(e) => setPickerAnchor(e.currentTarget as HTMLElement)}
                                title={intl.formatMessage({ id: 'note.insert.placeholder', defaultMessage: 'Inserir placeholder' })}
                                sx={{ p: 0.25, alignSelf: 'flex-start', mt: 0.5 }}
                            >
                                <CodeIcon fontSize="small" />
                            </IconButton>
                        ),
                    }}
                />
            </Box>

            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                onInsert={(placeholder) => {
                    insertAtCursor(activeInputRef.current as any, placeholder);
                    if (activeInputRef.current) {
                        const ev = new Event('input', { bubbles: true });
                        activeInputRef.current.dispatchEvent(ev);
                    }
                    setPickerAnchor(null);
                }}
            />
        </>
    );
};

export default NoteInspector;
